import {
  FORMULA_ERRORS,
  FormulaOperator,
  FormulaToken,
  FormulaValidation,
  OPERATORS,
  OperatorToken,
  aggregateLabel,
  canAddClosingParenthesis,
  canAddOpeningParenthesis,
  isOperatorTurn,
  validateFormula,
} from './formula';

export const TYPED_FORMULA_ERRORS = {
  missingOperator: 'modules.fieldConfig.formula.missingOperator',
  missingValue: 'modules.fieldConfig.formula.missingValue',
} as const;

const isParenthesis = (token: FormulaToken, value: '(' | ')') => token?.type === 'parenthesis' && token.value === value;
const isOperand = (token: FormulaToken) =>
  token.type === 'function' || token.type === 'date' || token.type === 'number';
const opensTerm = (token: FormulaToken) => isOperand(token) || isParenthesis(token, '(');
const closesTerm = (token: FormulaToken) => isOperand(token) || isParenthesis(token, ')');

/** Two values side by side, or an operator or parenthesis with no value next to it. */
const structureProblems = (tokens: FormulaToken[]): { index: number; error: string }[] => {
  const problems: { index: number; error: string }[] = opensTerm(tokens[0])
    ? []
    : [{ index: 0, error: TYPED_FORMULA_ERRORS.missingValue }];
  tokens.slice(1).forEach((token, position) => {
    const afterValue = closesTerm(tokens[position]);
    if (afterValue === opensTerm(token)) {
      problems.push({
        index: position + 1,
        error: afterValue ? TYPED_FORMULA_ERRORS.missingOperator : TYPED_FORMULA_ERRORS.missingValue,
      });
    }
  });
  return problems;
};

/**
 * The builder's validation, plus the structure a builder cannot get wrong but typing can. Broken
 * operands are still reported first, as legacy did; a structure problem comes before the end checks.
 */
export const validateTypedFormula = (tokens: FormulaToken[], isDateResult: boolean): FormulaValidation => {
  const validation = validateFormula(tokens, isDateResult);
  const operandsBroken = validation.errors.some(
    error => error === FORMULA_ERRORS.invalidFunction || error === FORMULA_ERRORS.invalidNumber,
  );
  const problems = tokens.length && !operandsBroken ? structureProblems(tokens) : [];
  if (!problems.length) {
    return validation;
  }
  return {
    errors: [...new Set(problems.map(problem => problem.error))],
    invalidIndexes: problems.map(problem => problem.index),
    result: undefined,
  };
};

// As legacy: a date function cannot come after an operator dates cannot take.
export const canAddDateFunction = (tokens: FormulaToken[]): boolean => {
  const lastOperator = [...tokens].reverse().find(token => token.type === 'operator') as OperatorToken;
  return !lastOperator || OPERATORS.find(entry => entry.value === lastOperator.value).forDate;
};

export interface FormulaInsertRules {
  /** A number, a field or a function. */
  value: boolean;
  date: boolean;
  open: boolean;
  close: boolean;
  operators: FormulaOperator[];
}

/**
 * What may go in at a position: the builder's rules for what may follow the tokens before it. The tokens
 * after it are not asked, or nothing could go in the middle (`5*` in front of `2+3` passes through `5 2+3`);
 * what that leaves broken is flagged by the validation. Only the date function's operators look both ways.
 */
export const insertableAt = (tokens: FormulaToken[], at: number, isDateResult: boolean): FormulaInsertRules => {
  if (isDateResult) {
    return { value: false, date: !tokens.length, open: false, close: false, operators: [] };
  }
  const before = tokens.slice(0, at);
  const after = tokens[at];
  const operatorTurn = isOperatorTurn(before);
  const nextToDate = before[before.length - 1]?.type === 'date' || after?.type === 'date';
  const datelessOperatorAfter =
    after?.type === 'operator' && !OPERATORS.find(entry => entry.value === after.value).forDate;
  return {
    value: !operatorTurn,
    date: !operatorTurn && !datelessOperatorAfter && canAddDateFunction(before),
    open: canAddOpeningParenthesis(before),
    close: canAddClosingParenthesis(before),
    operators: operatorTurn
      ? OPERATORS.filter(operator => operator.forDate || !nextToDate).map(operator => operator.value)
      : [],
  };
};

export const tokenText = (
  token: FormulaToken,
  datePart: (part: string) => string = part => part,
  missing = '…',
): string => {
  switch (token.type) {
    case 'function':
      return `${aggregateLabel(token.functionKey)}(${token.field?.value ?? missing})`;
    case 'date': {
      const second =
        token.secondKind === 'date' || token.useSecondField ? token.secondField?.value : token.secondNumber;
      return `${token.name}(${datePart(token.datePart)}, ${second ?? missing}, ${token.thirdField?.value ?? missing})`;
    }
    case 'operator':
      return OPERATORS.find(operator => operator.value === token.value).label;
    default:
      return String(token.value);
  }
};

export const formulaText = (tokens: FormulaToken[], datePart?: (part: string) => string, missing?: string): string =>
  tokens
    .map((token, index) => {
      const tight = !index || isParenthesis(tokens[index - 1], '(') || isParenthesis(token, ')');
      return (tight ? '' : ' ') + tokenText(token, datePart, missing);
    })
    .join('');
